// Kannada-only PDF text rasterization workaround.
//
// jsPDF has no text-shaping engine (no GSUB/GPOS, no Indic glyph reordering).
// Most scripts still render correctly because their font's glyphs are simple
// left-to-right advances, but Kannada's two-part dependent vowel signs (e.g.
// U+0CCB in "ಕೋಟಾ") are zero-advance-width composite glyphs designed to be
// positioned by a real shaping engine - jsPDF draws them stacked on top of
// the neighboring glyph instead of wrapped around it.
//
// The browser's own <canvas> text rendering uses the OS/engine's real text
// shaper (the same one that already renders the live page correctly), so
// Kannada PDF text is rasterized to a canvas and embedded as an image
// instead of drawn as jsPDF vector text. Every other language keeps using
// jsPDF's normal vector text path untouched.

const CANVAS_FONT_FAMILY = 'SugarHathiKannadaCanvas';
const RASTER_SCALE = 2; // render at 2x for crisp embedding without bloating PDF file size

let fontLoadPromise = null;
let sharedCanvas = null;
let sharedCtx = null;

function getCanvasCtx() {
  if (!sharedCanvas) {
    sharedCanvas = document.createElement('canvas');
    sharedCtx = sharedCanvas.getContext('2d');
  }
  return sharedCtx;
}

// Fetches the same static Kannada TTFs already used for jsPDF's vector font
// (public/fonts/) and registers them as a canvas-only font family via the
// FontFace API. Returns false on any failure - callers fall back to jsPDF's
// normal (imperfect-for-Kannada) vector text path rather than crash the PDF.
export async function ensureKannadaCanvasFont() {
  if (fontLoadPromise) return fontLoadPromise;

  fontLoadPromise = (async () => {
    try {
      const [normalBuf, boldBuf] = await Promise.all([
        fetch('/fonts/NotoSansKannada-Regular.ttf').then((r) => r.arrayBuffer()),
        fetch('/fonts/NotoSansKannada-Bold.ttf').then((r) => r.arrayBuffer()),
      ]);
      const normalFace = new FontFace(CANVAS_FONT_FAMILY, normalBuf, { weight: '400' });
      const boldFace = new FontFace(CANVAS_FONT_FAMILY, boldBuf, { weight: '700' });
      await Promise.all([normalFace.load(), boldFace.load()]);
      document.fonts.add(normalFace);
      document.fonts.add(boldFace);
      return true;
    } catch (e) {
      return false;
    }
  })();

  return fontLoadPromise;
}

// Rasterizes `text` to a PNG data URL using the browser's own text shaping.
// Returns null if the canvas font isn't loaded (caller should have checked
// ensureKannadaCanvasFont() first, but this stays defensive).
// fontSize/widths/heights are all in PDF points; the 4x raster scale is
// converted back to points on the way out so callers never see canvas pixels.
export function rasterKannadaText(text, { fontSize, bold, color }) {
  if (!text) return null;
  const ctx = getCanvasCtx();
  const weight = bold ? '700' : '400';
  const cssFont = `${weight} ${fontSize * RASTER_SCALE}px ${CANVAS_FONT_FAMILY}`;

  ctx.font = cssFont;
  const measured = ctx.measureText(text);
  const padPx = 3 * RASTER_SCALE;
  const ascentPx = Math.ceil(measured.actualBoundingBoxAscent || fontSize * RASTER_SCALE * 0.8);
  const descentPx = Math.ceil(measured.actualBoundingBoxDescent || fontSize * RASTER_SCALE * 0.25);
  const widthPx = Math.max(1, Math.ceil(measured.width) + padPx * 2);
  const heightPx = Math.max(1, ascentPx + descentPx + padPx * 2);

  sharedCanvas.width = widthPx;
  sharedCanvas.height = heightPx;
  // Resizing a canvas clears its context state, so font/fillStyle must be
  // re-applied after setting width/height.
  ctx.font = cssFont;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
  ctx.fillText(text, padPx, ascentPx + padPx);

  return {
    dataUrl: sharedCanvas.toDataURL('image/png'),
    widthPt: widthPx / RASTER_SCALE,
    heightPt: heightPx / RASTER_SCALE,
    ascentPt: (ascentPx + padPx) / RASTER_SCALE,
  };
}
